import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import '../../css/frontend/logo-page.css';

const apiUrl = process.env.REACT_APP_BACKEND_URL || '';

// Explicit allow-list, not a tag: a tag can be misapplied to an unrelated
// upload, but a key is chosen deliberately per resource, so only these two
// vetted keys ever appear on this page.
const RESOURCE_KEYS = ['logo_white_background', 'logo_image_transparent'];

const formatDateTime = (value) => {
    if (!value) return '—';

    try {
        return new Date(value).toLocaleString();
    } catch {
        return value;
    }
};

export default function LogoPage() {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [logos, setLogos] = useState([]);

    useEffect(() => {
        let active = true;

        const load = async () => {
            try {
                setLoading(true);
                setError('');

                const response = await fetch(`${apiUrl}/api/resources`);
                const data = await response.json();

                if (!response.ok) {
                    throw new Error(data?.error || 'Failed to load logos');
                }

                const byKey = new Map((data?.data || []).map((r) => [r.key, r]));
                const found = RESOURCE_KEYS.map((key) => byKey.get(key)).filter(Boolean);

                if (!active) return;

                setLogos(found);
            } catch (err) {
                console.error(err);
                if (active) setError(err.message || 'Failed to load logos');
            } finally {
                if (active) setLoading(false);
            }
        };

        load();

        return () => {
            active = false;
        };
    }, []);

    const previewImage = logos[0] ? `${apiUrl}${logos[0].href}` : null;

    return (
        <>
            <Helmet>
                <title>{t('LOGO_TITLE_PAGE') || 'SpaceTime Community | Logo'}</title>
                <meta
                    name="description"
                    content={t('LOGO_META_DESC') || 'The current SpaceTime logo, ready to download.'}
                />
                <meta property="og:title" content="SpaceTime | Logo" />
                <meta property="og:url" content="https://spacetime.community/logo" />
                {previewImage && <meta property="og:image" content={previewImage} />}
            </Helmet>

            <div className="lab-page logo-page">
                <header className="lab-map logo-hero">
                    <h1 className="lab-title">{t('LOGO_TITLE') || 'SpaceTime Logo'}</h1>
                    <p className="lab-subtitle">
                        {t('LOGO_SUBTITLE') ||
                            'Always the latest version — use this link when sharing with designers or partners.'}
                    </p>
                </header>

                <main className="logo-body">
                    {loading && <div className="logo-status">{t('Loading...') || 'Loading...'}</div>}

                    {!loading && !error && logos.length === 0 && (
                        <div className="logo-status">
                            {t('LOGO_NOT_FOUND') || 'No logo has been published yet.'}
                        </div>
                    )}

                    {!loading && error && (
                        <div className="logo-status logo-status--error">{error}</div>
                    )}

                    {!loading && !error && logos.length > 0 && (
                        <div className="logo-grid">
                            {logos.map((logo) => {
                                const fileHref = `${apiUrl}${logo.href}`;

                                return (
                                    <div className="logo-card" key={logo.key}>
                                        <img src={fileHref} alt={logo.title} className="logo-image" />

                                        <div className="logo-card-title">{logo.title}</div>

                                        <a href={fileHref} download className="logo-download-btn resource-link">
                                            {t('LOGO_DOWNLOAD') || 'Download'}
                                        </a>

                                        <div className="resource-last-updated logo-updated">
                                            {t('Last updated') || 'Last updated'}: {formatDateTime(logo.lastUpdated)}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </main>
            </div>
        </>
    );
}
